import { memo, useEffect, useState, useRef, useMemo } from 'react';

const DESKTOP_PARTICLE_COUNT = 8;
const MOBILE_PARTICLE_COUNT = 3;

const GlobalBackground = memo(() => {
  const [isSmallScreen, setIsSmallScreen] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768
  );
  const mouseRef = useRef({ x: 0, y: 0 });
  const glowRef = useRef(null);
  const gridRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const checkScreenSize = () => setIsSmallScreen(window.innerWidth < 768);
    window.addEventListener('resize', checkScreenSize, { passive: true });
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Use rAF-throttled mousemove instead of setState on every move
  useEffect(() => {
    if (isSmallScreen) return;

    const handleMouseMove = (e) => {
      mouseRef.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouseRef.current.y = (e.clientY / window.innerHeight) * 2 - 1;
    };

    const tick = () => {
      const { x, y } = mouseRef.current;
      if (glowRef.current) {
        glowRef.current.style.transform = `translate3d(${x * 25}px, ${y * 25}px, 0)`;
      }
      if (gridRef.current) {
        gridRef.current.style.transform = `translate3d(${x * -15}px, ${y * -15}px, 0)`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isSmallScreen]);

  // Pre-compute particle positions
  const particles = useMemo(() => {
    const count = isSmallScreen ? MOBILE_PARTICLE_COUNT : DESKTOP_PARTICLE_COUNT;
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      delay: `${Math.random() * 6}s`,
      duration: `${Math.random() * 10 + 18}s`,
    }));
  }, [isSmallScreen]);

  return (
    <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
      {/* Base gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black to-primary/10" />

      {/* Radial glows — CSS breathing + parallax on desktop */}
      <div className="absolute inset-0 global-bg-fadein">
        <div
          ref={glowRef}
          className="absolute inset-0 will-change-transform global-bg-breathe"
          style={{
            backgroundImage: `radial-gradient(circle at 50% 50%, rgba(255, 0, 0, 0.15) 0%, transparent 60%),
                              radial-gradient(circle at 0% 0%, rgba(255, 0, 0, 0.1) 0%, transparent 40%),
                              radial-gradient(circle at 100% 100%, rgba(255, 0, 0, 0.1) 0%, transparent 40%)`,
          }}
        />
      </div>

      {/* Grid pattern — desktop only, with subtle opacity pulse */}
      {!isSmallScreen && (
        <div
          ref={gridRef}
          className="absolute inset-0 will-change-transform global-bg-grid-pulse"
          style={{
            backgroundImage: `linear-gradient(rgba(255, 0, 0, 0.15) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(255, 0, 0, 0.15) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}
        />
      )}

      {/* Particles — CSS keyframe animation, reduced count on mobile */}
      <div className="absolute inset-0">
        {particles.map((p) => (
          <span
            key={p.id}
            className="absolute w-1 h-1 bg-primary/30 rounded-full global-bg-particle"
            style={{
              left: p.left,
              top: p.top,
              animationDelay: p.delay,
              animationDuration: p.duration,
            }}
          />
        ))}
      </div>
    </div>
  );
});

export default GlobalBackground;
